import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { shallowEqual } from 'react-redux'
import { useSelector } from '../../../../Flux/index.js'
import type { State } from '../../../../Flux/Slice/index.js'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import type { SourceId, SpinSelection } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { useGetAuthenticatedUser } from '../../../../Flux/Selector/index.js'
import { canUseSpin, canUseSpinOnOrder } from '../../../../Model/index.js'
import { PREFERRED_SPIN_ALGORITHM } from '../../../../Model/Spin/spinAlgorithm.js'
import { trackVizMode } from '../../../../Services/Telemetry/index.js'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.js'
import { useSpinAlgorithms } from '../../useSpinMeta.js'

export type SpinToggleState = 'on' | 'off' | 'mixed'

/** On only when every source reads SPINs; sources that disagree show as mixed. */
export function spinToggleState(spins: Array<SpinSelection | undefined>): SpinToggleState {
  const using = spins.filter((s) => s !== undefined).length
  if (using === 0) return 'off'
  return using === spins.length ? 'on' : 'mixed'
}

/** Switching on keeps a source's own algorithm and point in time; one without SPINs gets the panel's default. */
export function spinForSwitchOn(current: SpinSelection | undefined): SpinSelection {
  return current ?? { algorithmType: PREFERRED_SPIN_ALGORITHM, atTime: '' }
}

const TOOLTIP: Record<SpinToggleState, string> = {
  on: 'Every chart counts SPINs (canonical species clusters) instead of OTUs.',
  off: 'Count SPINs (canonical species clusters) instead of OTUs in every chart.',
  mixed: 'Some data sources use SPINs and some do not. Switch on to use SPINs everywhere.'
}

/** The workspace-wide SPIN switch: the one-click route to what each source's SPIN panel sets. */
export default function SpinQuickToggle() {
  const dispatch = useDispatch()
  const user = useGetAuthenticatedUser()
  const scope = useSelector((s: typeof State) => s.visualization.scope)
  const order = useSelector((s: typeof State) => (s.order?.order === 'LOADING' ? undefined : s.order?.order))
  const sources = useSelector(
    (s: typeof State) => Object.entries(s.visualization.sources).map(([id, src]) => [id as SourceId, src.shared?.spin] as const),
    shallowEqual
  )
  const inputRef = useRef<HTMLInputElement>(null)

  const scopeId = scope?.id ?? null
  const spinAccess = scope?.kind === 'order' ? canUseSpinOnOrder(order?.id === scopeId ? order : null, user) : canUseSpin(user)
  const { algorithms, error } = useSpinAlgorithms(scopeId, spinAccess)
  const state = spinToggleState(sources.map(([, spin]) => spin))

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = state === 'mixed'
  }, [state])

  if (!spinAccess || sources.length === 0) return null

  const noData = algorithms !== null && !algorithms.includes(PREFERRED_SPIN_ALGORITHM)
  const disabled = algorithms === null || noData || error
  const tooltip = error
    ? 'SPIN data could not be loaded.'
    : algorithms === null
      ? 'Checking for SPIN data…'
      : noData
        ? 'No SPINs for this data yet.'
        : TOOLTIP[state]

  const toggle = () => {
    const switchOn = state !== 'on'
    trackVizMode('spin', switchOn ? 'enable' : 'disable', { via: 'workspace_toggle', sources: sources.length })
    for (const [sourceId, spin] of sources) {
      dispatch(visualizationSlice.actions.updateSourceShared({ sourceId, patch: { spin: switchOn ? spinForSwitchOn(spin) : undefined } }))
    }
  }

  return (
    <TooltipTrigger tooltip={tooltip}>
      <label className={`flex items-center gap-2 text-sm whitespace-nowrap ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
        <input
          ref={inputRef}
          type="checkbox"
          data-testid="viz-spin-toggle"
          className="toggle toggle-sm toggle-primary"
          checked={state === 'on'}
          disabled={disabled}
          onChange={toggle}
        />
        SPINs
      </label>
    </TooltipTrigger>
  )
}
